import React, { useState, useEffect, useRef } from 'react';
import { User, LobbyState } from '../../types';
import { WS_BASE_URL } from '../../constants';
import LoadingSpinner from '../ui/LoadingSpinner';
import PlayerProgressBar from '../ui/PlayerProgressBar';
import OnScreenKeyboard from '../ui/OnScreenKeyboard';

interface MultiplayerRaceProps {
  user: User;
  classId: string | null;
}

const MultiplayerRace: React.FC<MultiplayerRaceProps> = ({ user, classId }) => {
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [lobbyState, setLobbyState] = useState<LobbyState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [typedText, setTypedText] = useState("");
  const [myProgress, setMyProgress] = useState({ wpm: 0, progress: 0 });
  const [pressedKey, setPressedKey] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (!classId) {
      setError("You must be in a class to race.");
      return;
    }
    const ws = new WebSocket(WS_BASE_URL);
    setSocket(ws);
    
    ws.onopen = () => { ws.send(JSON.stringify({ type: 'JOIN_LOBBY', payload: { token: localStorage.getItem('token'), classId } })); };
    ws.onmessage = (event) => {
      const message = JSON.parse(event.data);
      switch (message.type) {
        case 'LOBBY_STATE': setLobbyState(message.payload); break;
        case 'RACE_START': 
            setLobbyState(prev => prev ? { ...prev, raceText: message.payload.raceText, raceStarted: true } : null);
            startTimeRef.current = Date.now();
            inputRef.current?.focus();
            break;
        case 'PLAYER_PROGRESS':
            setLobbyState(prev => prev ? { ...prev, players: prev.players.map(p => p.userId === message.payload.userId ? { ...p, ...message.payload } : p) } : null);
            break;
        case 'ERROR': setError(message.payload); break;
      }
    };
    ws.onclose = () => setError("Disconnected from race server.");
    ws.onerror = () => setError("WebSocket connection error.");
    
    return () => ws.close();
  }, [classId]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lobbyState?.raceStarted || myProgress.progress >= 100) return;

      setPressedKey(e.key);
      setTimeout(() => setPressedKey(null), 100);

      e.preventDefault();

      if (e.key === 'Backspace') {
        setTypedText(prev => prev.slice(0, -1));
      } else if (e.key.length === 1) {
        setTypedText(prev => prev + e.key);
      }
    };

    const inputEl = inputRef.current;
    if (inputEl) {
      inputEl.addEventListener('keydown', handleKeyDown);
      return () => inputEl.removeEventListener('keydown', handleKeyDown);
    }
  }, [lobbyState?.raceStarted, myProgress.progress]);

  useEffect(() => {
    if (!lobbyState?.raceStarted || !startTimeRef.current) return;

    let correctChars = 0;
    const raceText = lobbyState.raceText || "";
    for (let i = 0; i < typedText.length; i++) {
        if (typedText[i] === raceText[i]) {
            correctChars++;
        }
    }

    let progress = raceText.length > 0 ? Math.round((typedText.length / raceText.length) * 100) : 0;
    if (typedText === raceText) {
        progress = 100;
    }
    
    const elapsedMinutes = (Date.now() - startTimeRef.current) / 60000;
    const wpm = elapsedMinutes > 0 ? Math.round((typedText.length / 5) / elapsedMinutes) : 0;
    
    const newProgress = { progress: Math.min(100, progress), wpm };
    setMyProgress(newProgress);
    socket?.send(JSON.stringify({ type: 'UPDATE_PROGRESS', payload: newProgress }));
  }, [typedText, lobbyState?.raceStarted, lobbyState?.raceText, socket]);

  const handleStartRace = () => socket?.send(JSON.stringify({ type: 'START_RACE' }));

  const renderRaceText = () => {
    if(!lobbyState || !lobbyState.raceText) return null;
    return lobbyState.raceText.split("").map((char, index) => {
      let charClass = "text-gray-400";
      if (index < typedText.length) charClass = typedText[index] === char ? "text-green-600" : "text-red-500 bg-red-100";
      else if (index === typedText.length) charClass = "text-blue-600 border-b-2 border-blue-600";
      if (char === ' ') charClass += " px-1";
      // Removed the \u00A0 so the browser is allowed to break lines at spaces
      return <span key={index} className={`font-mono text-2xl md:text-3xl ${charClass}`}>{char}</span>;
    });
  };

  if (error) return <p className="text-red-500 text-center">{error}</p>;
  if (!lobbyState) return <LoadingSpinner message="Joining race lobby..." />;

  const myPlayer = lobbyState.players.find(p => p.userId === user.user_id);
  const opponents = lobbyState.players.filter(p => p.userId !== user.user_id);
  const isFinished = myProgress.progress === 100;
  const allFinished = lobbyState.players.every(p => p.progress === 100);
  
  return (
    <div className="w-full max-w-4xl p-6 bg-white rounded-lg shadow-lg" onClick={() => inputRef.current?.focus()}>
      {!lobbyState.raceStarted ? (
        <div>
          <h2 className="text-2xl font-bold mb-4">Race Lobby</h2>
          <div className="space-y-2 mb-6">
            {lobbyState.players.map(p => <div key={p.userId} className="p-3 bg-gray-100 rounded-md">{p.email} {p.userId === user.user_id && "(You)"}</div>)}
          </div>
          <button onClick={handleStartRace} className="w-full px-6 py-3 bg-green-600 text-white rounded-lg font-semibold text-xl hover:bg-green-700">Start Race!</button>
        </div>
      ) : (
        <div>
          <h2 className="text-2xl font-bold mb-4">{allFinished ? "Race Finished!" : "Race!"}</h2>
          <div className="space-y-3 mb-6">
            {myPlayer && <PlayerProgressBar player={{ ...myPlayer, ...myProgress }} isMe={true} />}
            {opponents.map(p => <PlayerProgressBar key={p.userId} player={p} isMe={false} />)}
          </div>
          {allFinished ? (
            <div className="p-6 bg-green-100 rounded-lg text-center">
              <h3 className="text-2xl font-bold text-green-800">Results</h3>
              {[...lobbyState.players].sort((a,b) => b.wpm - a.wpm).map((p, index) => <div key={p.userId} className="text-lg mt-2"><span className="font-bold">{index + 1}.</span> {p.email} - {p.wpm} WPM</div>)}
            </div>
          ) : (
            <>
              {/* Added flex, flex-wrap, and gap to force the spans to wrap naturally like text */}
              <div className="p-4 md:p-6 mb-6 bg-gray-100 rounded-lg flex flex-wrap gap-y-2 text-2xl md:text-3xl leading-relaxed" style={{ minHeight: '150px', alignContent: 'flex-start' }}>{renderRaceText()}</div>
              <input ref={inputRef} type="text" className="opacity-0 absolute w-0 h-0" aria-hidden="true" autoFocus disabled={isFinished} />
              <OnScreenKeyboard nextChar={isFinished ? null : (lobbyState.raceText || "")[typedText.length]} isTutorial={false} lessonId={null} isError={false} lessonKeys={null} pressedKey={pressedKey} />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiplayerRace;